import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Globe,
  Search,
  Loader2,
  Check,
  AlertCircle,
  Palette,
  FileText,
  Tag,
  ArrowRight,
  ArrowLeft,
  ExternalLink
} from 'lucide-react';
import toast from 'react-hot-toast';
import onboardingService, { BusinessProfile, WebsiteAnalysisResult } from '../../services/onboardingService';
import { pollJobUntilComplete } from '../../services/aiJobService';

interface WebsiteStepProps {
  profile: BusinessProfile | null;
  updateProfile: (updates: Partial<BusinessProfile>) => void;
  onNext: () => void;
  onBack: () => void;
}

const WebsiteStep: React.FC<WebsiteStepProps> = ({
  profile,
  updateProfile,
  onNext,
  onBack
}) => {
  const { t } = useTranslation();
  const [url, setUrl] = useState(profile?.website_url || '');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Reconstruct analysis result from profile if available
  const [analysisResult, setAnalysisResult] = useState<WebsiteAnalysisResult | null>(() => {
    if (profile?.website_url && profile?.website_analysis) {
      return {
        profile: profile,
        analysis: {
          title: profile.website_analysis.title,
          description: profile.website_analysis.description,
          colors: profile.primary_colors,
          technologies: profile.website_analysis.technologies,
          seoScore: profile.website_analysis.seo_score,
          aiInsights: {
            industry: profile.industry,
            targetAudience: profile.target_audience?.description,
            suggestedKeywords: profile.auto_suggested_keywords?.map(k => k.keyword) || []
          }
        }
      };
    }
    return null;
  });

  const handleAnalyze = async () => {
    if (!url.trim()) {
      setError(t('onboarding.website.enterUrl', 'Please enter your website URL'));
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setProgress(0);

    try {
      const response = await onboardingService.analyzeWebsite(url);

      // Handle async background job
      if ('async' in response && response.async && response.jobId) {
        const jobResult = await pollJobUntilComplete(
          response.jobId,
          (prog) => setProgress(prog),
          2000,
          300000 // 5 minutes max
        );

        const data = jobResult?.data || jobResult;

        // Handle partial results (analysis had errors but returned gracefully)
        if (data?.partial) {
          toast.error(t('onboarding.website.partialAnalysis', 'Could not fully analyze the website, but you can continue.'));
          updateProfile({ website_url: formatUrl(url) } as any);
        } else {
          setAnalysisResult(data as WebsiteAnalysisResult);
          if (data?.profile) {
            updateProfile(data.profile);
          }
        }
      } else {
        // Sync response (fallback)
        const result = response as WebsiteAnalysisResult;
        setAnalysisResult(result);
        updateProfile(result.profile);
      }

      toast.success(t('onboarding.website.analyzeSuccess', 'Website analyzed successfully!'));
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || t('onboarding.website.analyzeError', 'Error analyzing website');
      setError(errorMessage);
      toast.error(errorMessage);
      // Even on error, allow user to proceed with just the URL
      updateProfile({ website_url: formatUrl(url) } as any);
    } finally {
      setIsAnalyzing(false);
      setProgress(0);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isAnalyzing) {
      handleAnalyze();
    }
  };

  const formatUrl = (value: string) => {
    if (!value.startsWith('http://') && !value.startsWith('https://')) {
      return `https://${value}`;
    }
    return value;
  };

  return (
    <div className="p-5 sm:p-12">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 mb-4 shadow-lg">
          <Globe className="h-8 w-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {t('onboarding.website.title', 'What is your business website?')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t('onboarding.website.subtitle', 'We will analyze your website to better understand your brand and market')}
        </p>
      </div>

      {/* URL Input */}
      <div className="max-w-xl mx-auto mb-8">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <Globe className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyPress={handleKeyPress}
            onBlur={() => url && setUrl(formatUrl(url))}
            placeholder={t('onboarding.website.placeholder', 'www.yourcompany.com')}
            className="block w-full pl-12 pr-4 py-4 text-lg border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            disabled={isAnalyzing}
          />
        </div>

        {error && (
          <div className="mt-3 flex items-center text-red-600 dark:text-red-400 text-sm">
            <AlertCircle className="h-4 w-4 mr-2" />
            {error}
          </div>
        )}

        <button
          onClick={handleAnalyze}
          disabled={isAnalyzing || !url.trim()}
          className="mt-4 w-full py-4 px-6 flex items-center justify-center text-white bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl font-semibold shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {isAnalyzing ? (
            <>
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              {t('onboarding.website.analyzing', 'Analyzing your website...')}
            </>
          ) : (
            <>
              <Search className="h-5 w-5 mr-2" />
              {t('onboarding.website.analyzeBtn', 'Analyze Website')}
            </>
          )}
        </button>
      </div>

      {/* Analysis Results */}
      {analysisResult && (
        <div className="max-w-2xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
          {/* Website Info Card */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {analysisResult?.analysis?.title || t('onboarding.website.yourSite', 'Your Website')}
                </h3>
                <a
                  href={analysisResult?.profile?.website_url || url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center"
                >
                  {analysisResult?.profile?.website_url || url}
                  <ExternalLink className="h-3 w-3 ml-1" />
                </a>
              </div>
              {analysisResult?.analysis?.seoScore && (
                <div className="text-right">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {analysisResult?.analysis?.seoScore}
                  </div>
                  <div className="text-xs text-gray-500">SEO Score</div>
                </div>
              )}
            </div>

            {analysisResult?.analysis?.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {analysisResult?.analysis?.description}
              </p>
            )}

            {/* Detected Info Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Colors */}
              {analysisResult?.analysis?.colors?.primary && (
                <div className="flex items-center space-x-3 p-3 bg-white dark:bg-gray-900/50 rounded-lg">
                  <Palette className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-xs text-gray-500 mb-1">{t('onboarding.website.detectedColors', 'Detected colors')}</div>
                    <div className="flex space-x-2">
                      {[analysisResult?.analysis?.colors.primary, analysisResult?.analysis?.colors.secondary]
                        .filter(Boolean)
                        .map((color, i) => (
                          <div
                            key={i}
                            className="w-6 h-6 rounded-full border-2 border-white shadow"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Industry */}
              {analysisResult?.analysis?.aiInsights?.industry && (
                <div className="flex items-center space-x-3 p-3 bg-white dark:bg-gray-900/50 rounded-lg">
                  <Tag className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-xs text-gray-500">{t('onboarding.website.detectedIndustry', 'Detected industry')}</div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {analysisResult?.analysis?.aiInsights.industry}
                    </div>
                  </div>
                </div>
              )}

              {/* Technologies */}
              {analysisResult?.analysis?.technologies && analysisResult?.analysis?.technologies.length > 0 && (
                <div className="flex items-center space-x-3 p-3 bg-white dark:bg-gray-900/50 rounded-lg">
                  <FileText className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="text-xs text-gray-500">{t('onboarding.website.technologies', 'Technologies')}</div>
                    <div className="font-medium text-gray-900 dark:text-white text-sm">
                      {analysisResult?.analysis?.technologies.slice(0, 2).map(t => t.name).join(', ')}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* AI Insights */}
          {analysisResult?.analysis?.aiInsights && (
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-6 border border-purple-200 dark:border-purple-700">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Check className="h-4 w-4 text-white" />
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-white">
                  {t('onboarding.website.aiInsights', 'AI Insights')}
                </h4>
              </div>

              <div className="space-y-3">
                {analysisResult?.analysis?.aiInsights.targetAudience && (
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('onboarding.website.targetAudience', 'Detected target audience:')}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {' '}{analysisResult?.analysis?.aiInsights.targetAudience}
                    </span>
                  </div>
                )}

                {analysisResult?.analysis?.aiInsights.suggestedKeywords && (
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('onboarding.website.suggestedKeywords', 'Suggested keywords:')}
                    </span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {analysisResult?.analysis?.aiInsights.suggestedKeywords.slice(0, 5).map((kw, i) => (
                        <span
                          key={i}
                          className="px-2 py-1 text-xs bg-white dark:bg-gray-800 rounded-full text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600"
                        >
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between items-center mt-10 pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onBack}
          className="flex items-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common.back', 'Back')}
        </button>

        <button
          onClick={onNext}
          disabled={!analysisResult && !profile?.website_url && !url.trim()}
          className="flex items-center px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-xl font-semibold shadow-lg shadow-primary-500/30 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {t('common.next', 'Continue')}
          <ArrowRight className="h-4 w-4 ml-2" />
        </button>
      </div>
    </div>
  );
};

export default WebsiteStep;
